import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LocationAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Search for a location...",
  className = "",
  disabled = false
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions from backend proxy (OpenStreetMap)
  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.length < 2) {
      setSuggestions([]);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/location/search?q=${encodeURIComponent(input)}`, {
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        setSuggestions([]);
        return;
      }

      const data = await response.json();
      setSuggestions(data.map(item => ({
        id: item.id,
        display_name: item.display_name,
        name: item.name || item.display_name.split(',')[0],
        type: item.type,
        address: item.address,
        lat: item.lat,
        lon: item.lon
      })));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Location search error:', error);
        setSuggestions([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue && !disabled) {
        fetchSuggestions(inputValue);
      } else {
        setSuggestions([]);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [inputValue, fetchSuggestions, disabled]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(true);
    onChange?.(newValue);
  };

  const handleSuggestionClick = (suggestion) => {
    const formattedAddress = suggestion.display_name;
    setInputValue(formattedAddress);
    onChange?.(formattedAddress, {
      lat: parseFloat(suggestion.lat),
      lng: parseFloat(suggestion.lon),
      fullAddress: suggestion.display_name
    });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleFocus = () => {
    if (suggestions.length > 0 && !disabled) {
      setShowSuggestions(true);
    }
  };

  const getPlaceIcon = (type) => {
    if (type === 'city' || type === 'town' || type === 'village') return '🏙️';
    if (type === 'venue' || type === 'building') return '🏢';
    if (type === 'administrative') return '🗺️';
    return '📍';
  };

  return (
    <div ref={wrapperRef} className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin z-10" />
      )}
      <Input
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={`pl-10 ${loading ? 'pr-10' : ''} ${className}`}
        autoComplete="off"
        disabled={disabled}
        data-testid="location-autocomplete-input"
      />
      
      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-white/10 rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              className="w-full px-4 py-3 text-left hover:bg-white/5 flex items-start gap-3 transition-colors"
              onClick={() => handleSuggestionClick(suggestion)}
              data-testid={`location-suggestion-${suggestion.id}`}
            >
              <span className="text-base mt-0.5 flex-shrink-0">
                {getPlaceIcon(suggestion.type)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{suggestion.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {suggestion.display_name.split(',').slice(1, 3).join(',')}
                </p>
              </div>
            </button>
          ))}
          <div className="px-4 py-2 text-xs text-muted-foreground border-t border-white/5 bg-secondary/30">
            Powered by OpenStreetMap
          </div>
        </div>
      )}

      {/* No results message */}
      {showSuggestions && inputValue.length >= 2 && !loading && suggestions.length === 0 && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-white/10 rounded-lg shadow-lg p-4 text-center text-sm text-muted-foreground">
          No locations found. Try a different search term.
        </div>
      )}
    </div>
  );
}
